import {
  IIndirectTaxFilingV2,
  IIndirectTaxFilingV2Amounts,
  IIndirectTaxFilingV2Contribution,
  IndirectTaxFilingV2ContributionBoxType,
} from '@commenda/api/indirect-tax/filings/types';
import { SalesTaxFilingStatusEnum } from '@prisma/client';

import { IndirectTaxRegionHelper } from '../../common';
import {
  IIndirectTaxFilingReturnSummary,
  IIndirectTaxFilingReturnSummaryRow,
  IIndirectTaxFilingReturnSummarySection,
} from '../types';

export const VAT_POSITION_SUMMARY_BUCKET = 'VAT_POSITION';
export const TAXABLE_SALES_SUMMARY_BUCKET = 'TAXABLE_SALES';

export class IndirectTaxFilingReturnSummaryHelper {
  // Amount-due for the filing's top metric. Filed filings show the paid
  // amount; anything else shows the estimated collected amount. Values are
  // in major currency units already (STA2 DAO scales picounits → dollars
  // before JSON emission).
  static resolveAmountDue(filing: IIndirectTaxFilingV2): number | null {
    const isFiled = filing.status === SalesTaxFilingStatusEnum.FILED;
    return isFiled ? filing.taxPaid : filing.estimatedTaxCollected;
  }

  // Contribution values arrive in minor currency units (cents, pence). The
  // /100 scale is the minor→major conversion for the currencies the filings
  // product supports today (USD, EUR, GBP, CAD all use 2 decimal places).
  //
  // When an operator has adjusted a box, `adjustedValue` overrides `value` so
  // the return summary (and the amount-due derived from it) reflect the
  // adjusted figures the agent submitted on the filing report.
  static contributionValueToMajor(
    contribution: IIndirectTaxFilingV2Contribution
  ): number {
    return (contribution.adjustedValue ?? contribution.value) / 100;
  }

  // US filing return summary — sourced from flat filing fields. Breakdown
  // rows always render (even at 0.00, to match the old /sales-tax review
  // page). Adjustment rows hide when null or zero.
  static buildReturnSummaryForUS(
    filing: IIndirectTaxFilingV2,
    currency: string
  ): IIndirectTaxFilingReturnSummary {
    const isFiled = filing.status === SalesTaxFilingStatusEnum.FILED;
    const pick = (
      filedKey: keyof IIndirectTaxFilingV2Amounts,
      estimatedKey: keyof IIndirectTaxFilingV2Amounts
    ): number | null => {
      const value = isFiled ? filing[filedKey] : filing[estimatedKey];
      return (value as number | null) ?? null;
    };

    const taxableSales = pick('taxableAmount', 'estimatedTaxableAmount');
    const nonTaxableSales = pick(
      'nonTaxableAmount',
      'estimatedNonTaxableAmount'
    );
    const marketplaceSales = pick(
      'marketplaceAmount',
      'estimatedMarketplaceAmount'
    );
    const exemptSales = pick('exemptAmount', 'estimatedExemptAmount');

    // No dedicated gross field on the filing — gross sales is the total across
    // all sale categories the breakdown splits out.
    const grossSales =
      (taxableSales ?? 0) +
      (nonTaxableSales ?? 0) +
      (marketplaceSales ?? 0) +
      (exemptSales ?? 0);

    const breakdownRows: IIndirectTaxFilingReturnSummaryRow[] = [
      { label: 'Gross sales', value: grossSales },
      { label: 'Taxable sales', value: taxableSales },
      { label: 'Non-taxable sales', value: nonTaxableSales },
      { label: 'Marketplace sales', value: marketplaceSales },
      { label: 'Exempt sales', value: exemptSales },
    ];

    // Labels mirror the legacy /sales-tax filing review page so users see
    // parity across the old and new filing detail pages.
    const adjustmentRows: IIndirectTaxFilingReturnSummaryRow[] = [
      { label: 'Prepayment', value: filing.prepayment },
      { label: 'Prior payment', value: filing.priorPayment },
      { label: 'Retail delivery fee', value: filing.retailDeliveryFee },
      {
        label: 'Jurisdiction-issued credit',
        value: filing.jurisdictionIssuedCredit,
      },
      { label: 'Marketplace payment', value: filing.marketplacePayment },
      { label: 'Vendor discount', value: filing.vendorDiscount },
      { label: 'Rounding', value: filing.rounding },
      { label: 'Penalty', value: filing.penalty },
      { label: 'Interest', value: filing.interest },
    ].filter((row) => row.value != null && row.value !== 0);

    return {
      sections: [
        { title: 'Breakdown', rows: breakdownRows },
        { title: 'Adjustments', rows: adjustmentRows },
      ],
      currency,
      amountDue:
        IndirectTaxFilingReturnSummaryHelper.resolveAmountDue(filing) ?? 0,
    };
  }

  // Render an applied filing rate (percentage x 10000, e.g. 190000) as a human
  // percent string ("19%", "5.5%", "0%"). Null when the box carries no concrete
  // rate (ANY / FORMULA boxes), so the caller can omit the suffix.
  static formatAppliedRate(appliedRate: number | null): string | null {
    if (appliedRate == null) {
      return null;
    }
    const percent = parseFloat((appliedRate / 10000).toFixed(4));
    return `${percent}%`;
  }

  // Convert a contribution row to a display row. The box name is suffixed with
  // its applied rate when the box resolves to a concrete percentage. This
  // matters for OSS reduced-rate boxes: the content holds a single bare-REDUCED
  // line, but the engine fans it into one contribution per distinct destination
  // rate — without the suffix those rows would share a label (identical text +
  // a colliding React key). Per-jurisdiction disambiguation still lives in the
  // enclosing section title.
  private static contributionToRow(
    contribution: IIndirectTaxFilingV2Contribution
  ): IIndirectTaxFilingReturnSummaryRow {
    const ratePercent = IndirectTaxFilingReturnSummaryHelper.formatAppliedRate(
      contribution.appliedRate
    );
    return {
      label: ratePercent
        ? `${contribution.boxName} (${ratePercent})`
        : contribution.boxName,
      value:
        IndirectTaxFilingReturnSummaryHelper.contributionValueToMajor(
          contribution
        ),
    };
  }

  // Sort a contribution list by box_order and emit rows. Both AGGREGATE and
  // FORMULA boxes are rendered together in a single stream — box_order from
  // the filing content is authoritative for display order. (FORMULA boxes in
  // contribution-driven returns are totals / derivations that belong inline
  // with the breakdown, not under "Adjustments".)
  private static contributionsToRows(
    contributions: Readonly<IIndirectTaxFilingV2Contribution[]>
  ): IIndirectTaxFilingReturnSummaryRow[] {
    return [...contributions]
      .sort((a, b) => a.boxOrder - b.boxOrder)
      .map(IndirectTaxFilingReturnSummaryHelper.contributionToRow);
  }

  // VAT return summary — sourced from filing contributions.
  //
  // Layout auto-selects based on the data, not the region:
  //   - No contribution carries a filing_through_jurisdiction → single
  //     "Breakdown" section. Used by country-level returns (DE Standard, UK
  //     VAT, …).
  //   - Any contribution carries a filing_through_jurisdiction → one section
  //     per destination jurisdiction, each titled with the full country name,
  //     sorted alphabetically by ISO code. Used by Union OSS / Non-Union OSS
  //     / IOSS returns, which legally require a per-destination-state block.
  //
  // Within each section, rows follow the content's `box_order`.
  //
  // Amount due:
  //   - Multi-jurisdiction: sum of FORMULA contributions (each jurisdiction's
  //     "Total VAT" formula). Matches what the seller owes on an OSS return.
  //   - Single-jurisdiction: contributions there are typically all AGGREGATE,
  //     and the filing-row total (resolveAmountDue) is authoritative.
  static buildReturnSummaryForVAT(
    filing: IIndirectTaxFilingV2,
    contributions: Readonly<IIndirectTaxFilingV2Contribution[]>,
    currency: string
  ): IIndirectTaxFilingReturnSummary {
    const isMultiJurisdiction = contributions.some(
      (c) => c.filingThroughJurisdiction != null
    );

    let sections: IIndirectTaxFilingReturnSummarySection[];

    if (!isMultiJurisdiction) {
      sections = [
        {
          title: 'Breakdown',
          rows: IndirectTaxFilingReturnSummaryHelper.contributionsToRows(
            contributions
          ),
        },
      ];
    } else {
      const byJurisdiction = new Map<
        string,
        IIndirectTaxFilingV2Contribution[]
      >();
      for (const contribution of contributions) {
        // Unjurisdictioned rows on a multi-jurisdiction filing are unexpected
        // but we keep them under a sentinel so they can't silently vanish.
        const key = contribution.filingThroughJurisdiction ?? 'Unspecified';
        const list = byJurisdiction.get(key) ?? [];
        list.push(contribution);
        byJurisdiction.set(key, list);
      }

      const sortedJurisdictions = Array.from(byJurisdiction.keys()).sort();
      sections = sortedJurisdictions.map((jurisdiction) => ({
        title: IndirectTaxRegionHelper.getCountryName(jurisdiction),
        rows: IndirectTaxFilingReturnSummaryHelper.contributionsToRows(
          byJurisdiction.get(jurisdiction) ?? []
        ),
      }));
    }

    return {
      sections,
      currency,
      // Prefer the explicit VAT_POSITION-tagged contribution(s); fall back to
      // the highest-boxOrder FORMULA heuristic for filings whose content
      // predates the summary_bucket tag.
      amountDue:
        IndirectTaxFilingReturnSummaryHelper.resolveVatPositionFromContributions(
          contributions
        ) ??
        IndirectTaxFilingReturnSummaryHelper.computeVatAmountFromContributions(
          contributions
        ) ??
        0,
    };
  }

  static resolveVatPositionFromContributions(
    contributions: Readonly<IIndirectTaxFilingV2Contribution[]>
  ): number | null {
    return IndirectTaxFilingReturnSummaryHelper.resolveSummaryBucketFromContributions(
      contributions,
      VAT_POSITION_SUMMARY_BUCKET
    );
  }

  // Sum (in major units) of the contributions tagged with `summaryBucket`;
  // null when the content tags no box with that bucket.
  static resolveSummaryBucketFromContributions(
    contributions: Readonly<IIndirectTaxFilingV2Contribution[]>,
    summaryBucket: string
  ): number | null {
    const rows = contributions.filter(
      (contribution) => contribution.summaryBucket === summaryBucket
    );
    if (rows.length === 0) return null;

    return rows.reduce(
      (total, row) =>
        total +
        IndirectTaxFilingReturnSummaryHelper.contributionValueToMajor(row),
      0
    );
  }

  // Total VAT payable derived from contribution data. Used wherever we need
  // an amount-due for an EU / UK filing — by contract,
  // `filing.estimatedTaxCollected` is 0 for those regions and must not be
  // read.
  //
  // Strategy: within each jurisdiction, pick the FORMULA box with the
  // highest `box_order` — by content-library convention, that box is the
  // final Total VAT payable (e.g. DE_83 "Final VAT position" at order 98,
  // UOSS_TOTAL / NUOSS_TOTAL / IOSS_TOTAL at order 90). Summing these
  // per-jurisdiction totals yields the filing's total VAT.
  //
  // Why not "sum all FORMULA": country returns (DE, UK, …) have intermediate
  // FORMULA boxes (residuals, running subtotals, chain references) that
  // would double-count. Picking the last FORMULA is the closest thing to
  // "the bottom line on the return" without a dedicated `is_total` flag on
  // the content view.
  //
  // Returns null when no FORMULA contributions are present (e.g. list
  // endpoint was called without extend=filing_contribution) — callers can
  // then fall back or render a dash.
  static computeVatAmountFromContributions(
    contributions: Readonly<IIndirectTaxFilingV2Contribution[]>
  ): number | null {
    const formulasByJurisdiction = new Map<
      string,
      IIndirectTaxFilingV2Contribution[]
    >();
    for (const contribution of contributions) {
      if (
        contribution.boxType !== IndirectTaxFilingV2ContributionBoxType.FORMULA
      ) {
        continue;
      }
      const key = contribution.filingThroughJurisdiction ?? '';
      const list = formulasByJurisdiction.get(key) ?? [];
      list.push(contribution);
      formulasByJurisdiction.set(key, list);
    }

    if (formulasByJurisdiction.size === 0) return null;

    return Array.from(formulasByJurisdiction.values()).reduce(
      (total, formulaRows) => {
        const final = formulaRows.reduce(
          (max, row) => (row.boxOrder > max.boxOrder ? row : max),
          formulaRows[0]
        );
        return (
          total +
          IndirectTaxFilingReturnSummaryHelper.contributionValueToMajor(final)
        );
      },
      0
    );
  }
}
